import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { Camera, Upload, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const DropzoneContainer = styled.div`
  border: 3px dashed ${props => props.isDragActive ? '#3a5cf7' : '#cbd5e1'};
  border-radius: 20px;
  padding: 4rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.isDragActive 
    ? 'linear-gradient(135deg, rgba(58, 92, 247, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)' 
    : 'linear-gradient(135deg, rgba(248, 250, 252, 0.5) 0%, rgba(241, 245, 249, 0.3) 100%)'};
  width: 100%;
  max-width: 600px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(58, 92, 247, 0.1) 0%, transparent 70%);
    opacity: ${props => props.isDragActive ? 1 : 0};
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    border-color: #3a5cf7;
    background: linear-gradient(135deg, rgba(58, 92, 247, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%);
    transform: scale(1.02);
    box-shadow: 0 10px 25px -5px rgba(58, 92, 247, 0.2);
  }
`;

const UploadIcon = styled.div`
  width: 96px;
  height: 96px;
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: #3a5cf7;
  border: 3px solid rgba(58, 92, 247, 0.2);
  box-shadow: 0 8px 16px -4px rgba(58, 92, 247, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  
  ${DropzoneContainer}:hover & {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 12px 24px -4px rgba(58, 92, 247, 0.3);
  }
`;

const UploadText = styled.div`
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;
  letter-spacing: -0.01em;
`;

const UploadSubtext = styled.div`
  color: #64748b;
  font-size: 0.95rem;
  position: relative;
  z-index: 1;
`;

const FileInput = styled.input`
  display: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #3a5cf7 0%, #2d4ae8 100%);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(58, 92, 247, 0.3), 0 2px 4px -1px rgba(58, 92, 247, 0.2);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }
    
    &:hover {
      background: linear-gradient(135deg, #2d4ae8 0%, #1e3a8a 100%);
      transform: translateY(-3px);
      box-shadow: 0 12px 24px -4px rgba(58, 92, 247, 0.4);
      
      &::before {
        left: 100%;
      }
    }
    
    &:active {
      transform: translateY(-1px);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: white;
    color: #475569;
    border: 2px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
`;

const LoadingText = styled.div`
  color: #0f172a;
  font-weight: 600;
  font-size: 1.125rem;
  letter-spacing: -0.01em;
`;

const LoadingSubtext = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PreviewImage = styled.img`
  max-width: 400px;
  max-height: 400px;
  border-radius: 20px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  border: 4px solid white;
  animation: fadeInScale 0.5s ease-out;
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

function ImageUpload({ onAnalysisComplete, isLoading, setIsLoading }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setPreview(null); // Clear previous preview immediately
      
      // Create preview - simple and fast
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    disabled: isLoading
  });

  const handleCameraCapture = () => {
    // In a real app, this would open the camera
    // For now, we'll just trigger the file input
    document.getElementById('camera-input').click();
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get or create session ID
      const { getSessionId, getUserId } = require('../utils/analytics');
      const sessionId = getSessionId();
      const userId = getUserId();

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('session_id', sessionId);
      formData.append('user_id', userId);

      const response = await axios.post(`${API_URL}/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
      });

      onAnalysisComplete(response.data);
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Analysis timed out. Please try with a smaller image or check your connection.');
      } else {
        setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <div style={{ 
          width: '80px', 
          height: '80px', 
          borderRadius: '50%', 
          background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 16px -4px rgba(58, 92, 247, 0.2)'
        }}>
          <Loader size={48} className="animate-spin" style={{ color: '#3a5cf7' }} />
        </div>
        <LoadingText>Analyzing your facial features...</LoadingText>
        <LoadingSubtext>
          This may take a few moments
        </LoadingSubtext>
      </LoadingContainer>
    );
  }

  return (
    <UploadContainer>
      {!selectedFile ? (
        <>
          <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <UploadIcon>
              <Upload size={32} />
            </UploadIcon>
            <UploadText>
              {isDragActive ? 'Drop your photo here' : 'Drag & drop your photo here'}
            </UploadText>
            <UploadSubtext>
              or click to browse files
            </UploadSubtext>
          </DropzoneContainer>

          <ButtonGroup>
            <ActionButton variant="secondary" onClick={handleCameraCapture}>
              <Camera size={20} />
              Take Selfie
            </ActionButton>
            <FileInput
              id="camera-input"
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => {
                if (e.target.files[0]) {
                  onDrop([e.target.files[0]]);
                }
              }}
            />
          </ButtonGroup>
        </>
      ) : (
        <>
          <PreviewImage src={preview} alt="Selected photo" />
          <ButtonGroup>
            <ActionButton variant="primary" onClick={handleAnalyze} disabled={isLoading}>
              <Upload size={20} />
              Analyze Photo
            </ActionButton>
            <ActionButton variant="secondary" onClick={handleReset} disabled={isLoading}>
              Choose Different Photo
            </ActionButton>
          </ButtonGroup>
        </>
      )}

      {error && (
        <ErrorMessage>
          <AlertCircle size={20} />
          {error}
        </ErrorMessage>
      )}
    </UploadContainer>
  );
}

export default ImageUpload;
